/**
 * Les journaux et les écritures, tels que l'écran les lit — et ce qu'il en
 * dit sans aller-retour.
 *
 * ## Un écran de lecture
 *
 * Rien ici n'écrit. Les écritures naissent des gestes de la base
 * (`journal_des_ventes()`, `comptabiliser_reprise()`, `extourner_ecriture()`,
 * et demain la saisie d'OD) ; l'écran les montre telles qu'elles sont. Un
 * brouillon déséquilibré se lit déséquilibré, une ligne de produit sans centre
 * se lit sans centre, un journal à trou se lit « numérotation interrompue » :
 * l'écran ne complète rien et ne corrige rien.
 *
 * ## Les formes viennent de la migration
 *
 * `20260928134858_la_lecture_des_journaux.sql` : `v_journaux`,
 * `v_liste_des_ecritures`, `v_lignes_ecriture`. `database.types.ts` ne connaît
 * pas le schéma `compta` ; les formes sont déclarées ici, à la main.
 */

import { avecRecherche, CHEMINS } from '../../navigation/chemins';
import { nettoyerPourIlike } from '../../factures/recherche';
import { controlerEcriture, type Controle } from '../equilibre';

/**
 * Les colonnes que l'écran lit, vue par vue, telles que la migration
 * `20260928134858` les nomme. Un test les confronte au fichier : une colonne
 * renommée d'un côté casse le test, pas l'écran en production.
 */
export const COLONNES = {
  journaux: [
    'exercice', 'exercice_statut', 'journal_code', 'journal_libelle', 'journal_type',
    'compte_tresorerie_code', 'journal_actif', 'ordre', 'nb_validees', 'nb_brouillons', 'nb_extournes',
    'total_debit_cents', 'total_credit_cents', 'premier_numero', 'dernier_numero', 'compteur',
    'numerotation_continue', 'premiere_date', 'derniere_date', 'derniere_validation',
  ],
  liste: [
    'id', 'journal_code', 'exercice', 'periode', 'date_ecriture', 'numero', 'reference', 'libelle',
    'nature', 'statut', 'piece_reference', 'source_type', 'source_id', 'source_evenement',
    'source_generation', 'regle_version', 'processus', 'date_origine', 'exercice_origine',
    'correction_anterieure', 'ecriture_corrigee_id', 'extournee_par_id', 'total_debit_cents',
    'total_credit_cents', 'nb_lignes', 'comptes', 'tiers_ids', 'tiers_noms', 'facture_numero',
    'created_at', 'valide_le',
  ],
  lignes: [
    'ligne_id', 'ecriture_id', 'ecriture_statut', 'position', 'compte_code', 'compte_libelle', 'classe',
    'tiers_id', 'tiers_nom', 'tiers_type', 'debit_cents', 'credit_cents', 'libelle', 'source_type',
    'source_id', 'ligne_corrigee_id', 'ventilations',
  ],
} as const;

export type StatutEcriture = 'brouillon' | 'validee';
export type NatureEcriture = 'ordinaire' | 'ouverture' | 'regularisation' | 'cloture' | 'extourne';
export type TypeDeJournal =
  | 'ouverture' | 'ventes' | 'achats' | 'financier' | 'operations_diverses' | 'cloture';

/** Un journal dans un exercice : une ligne de `compta.v_journaux`. */
export interface JournalDeLExercice {
  exercice: number;
  exerciceStatut: string;
  code: string;
  libelle: string;
  type: TypeDeJournal;
  compteTresorerie: string | null;
  actif: boolean;
  ordre: number;
  nbValidees: number;
  nbBrouillons: number;
  nbExtournes: number;
  /** Des écritures validées seulement. */
  totalDebitCents: number;
  totalCreditCents: number;
  premierNumero: number | null;
  dernierNumero: number | null;
  compteur: number | null;
  /** De 1 au dernier numéro, sans trou, et le compteur d'accord. */
  numerotationContinue: boolean;
  premiereDate: string | null;
  derniereDate: string | null;
  derniereValidation: string | null;
}

/** Une ligne de `compta.v_liste_des_ecritures`. */
export interface EcritureListee {
  id: string;
  journal: string;
  exercice: number;
  periode: number;
  date: string;
  numero: number | null;
  /** `VEN-2026-002709` ; `null` pour un brouillon, qui n'est pas numéroté. */
  reference: string | null;
  libelle: string;
  nature: NatureEcriture;
  statut: StatutEcriture;
  piece: string | null;
  sourceType: string | null;
  sourceId: string | null;
  sourceEvenement: string | null;
  sourceGeneration: number;
  regleVersion: string | null;
  processus: string | null;
  dateOrigine: string | null;
  exerciceOrigine: number | null;
  correctionAnterieure: boolean;
  /** L'écriture que celle-ci extourne. */
  corrigeeId: string | null;
  /** L'extourne qui annule celle-ci. */
  extourneeParId: string | null;
  totalDebitCents: number;
  totalCreditCents: number;
  nbLignes: number;
  comptes: string[];
  tiersIds: string[];
  tiersNoms: string | null;
  /** La facture traduite : la sienne, ou celle qu'une note crédite. */
  factureNumero: string | null;
  creeLe: string;
  valideeLe: string | null;
}

export interface Ventilation {
  axeCode: string;
  axeLibelle: string;
  valeurCode: string;
  valeurLibelle: string;
  montantCents: number;
}

/** Une ligne de `compta.v_lignes_ecriture`. */
export interface LigneEcriture {
  id: string;
  ecritureId: string;
  position: number;
  compteCode: string;
  compteLibelle: string;
  classe: number;
  tiersId: string | null;
  tiersNom: string | null;
  tiersType: string | null;
  debitCents: number;
  creditCents: number;
  libelle: string | null;
  sourceType: string | null;
  sourceId: string | null;
  ligneCorrigeeId: string | null;
  ventilations: Ventilation[];
}

// ---------------------------------------------------------------------------
//  Les libellés
// ---------------------------------------------------------------------------

export const STATUTS_ECRITURE: Record<StatutEcriture, { label: string; classe: string }> = {
  brouillon: { label: 'Brouillon', classe: 'bg-amber-100 text-amber-800' },
  validee: { label: 'Validée', classe: 'bg-emerald-100 text-emerald-800' },
};

export const NATURES: Record<NatureEcriture, string> = {
  ordinaire: 'Ordinaire',
  ouverture: 'Ouverture',
  regularisation: 'Régularisation',
  cloture: 'Clôture',
  extourne: 'Extourne',
};

export const TYPES_DE_JOURNAL: Record<TypeDeJournal, string> = {
  ouverture: 'Ouverture',
  ventes: 'Ventes',
  achats: 'Achats',
  financier: 'Financier',
  operations_diverses: 'Opérations diverses',
  cloture: 'Clôture',
};

export const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
] as const;

/** Ce qu'on affiche à la place d'une référence : un brouillon n'a pas de numéro. */
export function referenceAffichee(e: Pick<EcritureListee, 'reference' | 'statut'>): string {
  return e.reference ?? 'Brouillon';
}

// ---------------------------------------------------------------------------
//  Les filtres de la liste, portés par l'adresse
// ---------------------------------------------------------------------------

export type Tri = 'recentes' | 'chronologique';

export const TRIS: Record<Tri, { label: string; ascendant: boolean }> = {
  recentes: { label: 'Plus récentes d’abord', ascendant: false },
  chronologique: { label: 'Ordre chronologique', ascendant: true },
};

export interface FiltresEcritures {
  exercice: number | null;
  journal: string | null;
  statut: StatutEcriture | null;
  periode: number | null;
  compte: string | null;
  recherche: string;
  tri: Tri;
}

export const SANS_FILTRE: FiltresEcritures = {
  exercice: null, journal: null, statut: null, periode: null, compte: null, recherche: '', tri: 'recentes',
};

/**
 * Les filtres qu'une adresse porte. Une valeur mal formée est **ignorée**,
 * pas devinée : `?journal=ven` ne désigne pas `VEN`, et `?periode=13` rien du
 * tout — l'écran montre alors la liste sans ce filtre, ce qui est vrai.
 */
export function lireFiltres(p: URLSearchParams): FiltresEcritures {
  const exercice = p.get('exercice');
  const journal = p.get('journal');
  const statut = p.get('statut');
  const periode = p.get('periode');
  const compte = p.get('compte');
  const tri = p.get('tri');
  return {
    exercice: exercice && /^\d{4}$/.test(exercice) ? Number(exercice) : null,
    journal: journal && /^[A-Z0-9]{2,10}$/.test(journal) ? journal : null,
    statut: statut === 'brouillon' || statut === 'validee' ? statut : null,
    periode: periode && /^(?:[1-9]|1[0-2])$/.test(periode) ? Number(periode) : null,
    compte: compte && /^\d{1,10}$/.test(compte) ? compte : null,
    recherche: (p.get('q') ?? '').trim(),
    tri: tri === 'chronologique' ? 'chronologique' : 'recentes',
  };
}

/** L'inverse de `lireFiltres` : seuls les filtres posés entrent dans l'adresse. */
export function versParametres(f: Partial<FiltresEcritures>): URLSearchParams {
  const p = new URLSearchParams();
  if (f.exercice) p.set('exercice', String(f.exercice));
  if (f.journal) p.set('journal', f.journal);
  if (f.statut) p.set('statut', f.statut);
  if (f.periode) p.set('periode', String(f.periode));
  if (f.compte) p.set('compte', f.compte);
  const q = (f.recherche ?? '').trim();
  if (q) p.set('q', q);
  if (f.tri && f.tri !== 'recentes') p.set('tri', f.tri);
  return p;
}

/** La liste des écritures ouverte sur des filtres — le lien d'un journal, d'un brouillon… */
export function lienVersLesEcritures(f: Partial<FiltresEcritures> = {}): string {
  const q = versParametres(f).toString();
  return q ? `${CHEMINS.comptabilite.ecritures}?${q}` : CHEMINS.comptabilite.ecritures;
}

// ---------------------------------------------------------------------------
//  La recherche
// ---------------------------------------------------------------------------

/** Trois caractères au minimum, comme l'écran des factures. */
export const MIN_CARACTERES = 3;

export interface RechercheEcritures {
  /** Ce qu'il faut passer à `recherche.ilike.*…*`, en minuscules ; `null` : pas de recherche. */
  motif: string | null;
  explication: string | null;
}

/**
 * Ce que la barre de recherche a compris.
 *
 * Une référence se tape comme on la lit sur un listing — `VEN-2026-2709` —
 * et se stocke avec son numéro sur six chiffres, `VEN-2026-002709`. Chercher
 * la saisie brute ne trouverait rien et laisserait croire que l'écriture
 * n'existe pas : la référence est donc remise à sa forme stockée. Tout le
 * reste (nom de famille, numéro de facture, compte, libellé) se cherche tel
 * quel, dans le champ `recherche` de la vue.
 */
export function lireRechercheEcritures(saisie: string | null | undefined): RechercheEcritures {
  const t = (saisie ?? '').trim();
  if (t === '') return { motif: null, explication: null };

  const ref = /^([A-Za-z0-9]{2,10})[-\s](\d{4})[-\s](\d{1,6})$/.exec(t);
  if (ref) {
    const reference = `${ref[1].toUpperCase()}-${ref[2]}-${ref[3].padStart(6, '0')}`;
    return { motif: reference.toLowerCase(), explication: `L’écriture ${reference}` };
  }

  if (t.length < MIN_CARACTERES) {
    return { motif: null, explication: `${MIN_CARACTERES} caractères au minimum pour chercher.` };
  }
  const propre = nettoyerPourIlike(t);
  if (propre.length < MIN_CARACTERES) return { motif: null, explication: null };
  return {
    motif: propre.toLowerCase(),
    explication: `Référence, libellé, pièce, facture, tiers ou compte contenant « ${propre} »`,
  };
}

// ---------------------------------------------------------------------------
//  D'où vient l'écriture
// ---------------------------------------------------------------------------

export interface LienDeSource {
  libelle: string;
  /** `null` : la source se nomme, mais aucun écran ne la montre encore. */
  chemin: string | null;
}

/**
 * Le fait source de l'écriture, et l'écran qui le montre. Une note de crédit
 * s'affiche sous sa facture : c'est la facture qu'on cherche.
 *
 * Une extourne n'a pas de source propre : sa chaîne (l'écriture qu'elle
 * annule) se montre à part, dans les deux sens.
 */
export function lienDeLaSource(
  e: Pick<EcritureListee, 'sourceType' | 'sourceEvenement' | 'piece' | 'factureNumero' | 'nature'>,
): LienDeSource | null {
  switch (e.sourceType) {
    case 'invoice': {
      const numero = e.factureNumero ?? e.piece;
      return {
        libelle: `Facture ${numero ?? '(sans numéro)'}`,
        chemin: numero ? avecRecherche(CHEMINS.comptabilite.factures, numero) : null,
      };
    }
    case 'credit_note': {
      const correction = e.sourceEvenement === 'correction_anterieure' ? ' — correction d’un exercice antérieur' : '';
      return {
        libelle: `Note de crédit ${e.piece ?? '(sans numéro)'}${e.factureNumero ? ` sur ${e.factureNumero}` : ''}${correction}`,
        chemin: e.factureNumero ? avecRecherche(CHEMINS.comptabilite.factures, e.factureNumero) : null,
      };
    }
    case 'reprise':
      return { libelle: 'Reprise d’ouverture', chemin: CHEMINS.comptabilite.reprise };
    case 'payment':
    case 'refund': {
      /* Le journal de trésorerie : l'écriture de banque d'un mouvement, ou un
         reclassement en OD dont la pièce est le constat (« Constat 2 — ING-2026-000457 »). */
      const mouvement = e.sourceType === 'payment' ? 'Encaissement bancaire' : 'Sortie bancaire';
      const rang = /^reclassement:(\d+)$/.exec(e.sourceEvenement ?? '')?.[1];
      return {
        libelle: rang
          ? `Reclassement, constat ${rang} — ${mouvement.toLowerCase()}${e.piece ? ` (${e.piece})` : ''}`
          : `${mouvement}${e.piece ? ` ${e.piece}` : ''}`,
        /* Pas encore d'écran d'un mouvement seul : la fiche se suffit. */
        chemin: null,
      };
    }
    case null:
    case undefined:
      if (e.nature === 'extourne') return null;
      return e.piece ? { libelle: `Pièce ${e.piece}`, chemin: null } : null;
    default:
      return { libelle: `${e.sourceType}${e.sourceEvenement ? ` · ${e.sourceEvenement}` : ''}`, chemin: null };
  }
}

// ---------------------------------------------------------------------------
//  Les lignes : équilibre et analytique
// ---------------------------------------------------------------------------

/**
 * Le contrôle des montants d'une écriture, par la même règle que la saisie
 * (`controlerEcriture`). Pour une écriture validée, la base l'a déjà rendu :
 * il ne peut qu'être juste. Pour un brouillon, il dit l'écart.
 */
export function controleDesLignes(lignes: readonly Pick<LigneEcriture, 'compteCode' | 'debitCents' | 'creditCents'>[]): Controle {
  return controlerEcriture(lignes.map((l) => ({
    compte: l.compteCode, debitCents: l.debitCents, creditCents: l.creditCents,
  })));
}

export interface AxeDeLaLigne {
  axeCode: string;
  axeLibelle: string;
  valeurs: Ventilation[];
  ventileCents: number;
  /** Montant de la ligne moins ce qui est ventilé sur cet axe : jamais caché. */
  resteCents: number;
}

export interface AnalytiqueDeLaLigne {
  montantCents: number;
  axes: AxeDeLaLigne[];
  /**
   * Une ligne de charge (classe 6) ou de produit (classe 7) qui ne porte
   * aucune ventilation. L'écran le dit ; il ne décide pas qu'elle devrait en
   * porter une (quels axes sont attendus reste à fixer).
   */
  resultatSansVentilation: boolean;
}

/** Les ventilations d'une ligne, regroupées par axe dans l'ordre reçu, avec leur reste. */
export function analytiqueDeLaLigne(
  l: Pick<LigneEcriture, 'debitCents' | 'creditCents' | 'classe' | 'ventilations'>,
): AnalytiqueDeLaLigne {
  const montantCents = l.debitCents + l.creditCents;
  const axes: AxeDeLaLigne[] = [];
  for (const v of l.ventilations) {
    let axe = axes.find((a) => a.axeCode === v.axeCode);
    if (!axe) {
      axe = { axeCode: v.axeCode, axeLibelle: v.axeLibelle, valeurs: [], ventileCents: 0, resteCents: montantCents };
      axes.push(axe);
    }
    axe.valeurs.push(v);
    axe.ventileCents += v.montantCents;
    axe.resteCents = montantCents - axe.ventileCents;
  }
  return {
    montantCents,
    axes,
    resultatSansVentilation: (l.classe === 6 || l.classe === 7) && l.ventilations.length === 0,
  };
}

// ---------------------------------------------------------------------------
//  Les journaux d'un exercice
// ---------------------------------------------------------------------------

export interface TotalDesJournaux {
  nbValidees: number;
  nbBrouillons: number;
  totalDebitCents: number;
  totalCreditCents: number;
  /** Tous les journaux de l'exercice sont numérotés sans trou. */
  continus: boolean;
}

export function totalDesJournaux(journaux: readonly JournalDeLExercice[]): TotalDesJournaux {
  return journaux.reduce<TotalDesJournaux>(
    (t, j) => ({
      nbValidees: t.nbValidees + j.nbValidees,
      nbBrouillons: t.nbBrouillons + j.nbBrouillons,
      totalDebitCents: t.totalDebitCents + j.totalDebitCents,
      totalCreditCents: t.totalCreditCents + j.totalCreditCents,
      continus: t.continus && j.numerotationContinue,
    }),
    { nbValidees: 0, nbBrouillons: 0, totalDebitCents: 0, totalCreditCents: 0, continus: true },
  );
}

/** L'exercice que l'écran ouvre par défaut : le plus récent qui n'est pas clôturé, sinon le plus récent. */
export function exerciceParDefaut(exercices: readonly { annee: number; statut: string }[]): number | null {
  if (exercices.length === 0) return null;
  const tries = [...exercices].sort((a, b) => b.annee - a.annee);
  return (tries.find((e) => e.statut !== 'cloture') ?? tries[0]).annee;
}
